import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  DashboardCard,
  PageHeader,
  SectionHeader,
  Skeleton,
} from '@zafroi/ui-kit';
import {
  ArrowLeft,
  CheckCircle2,
  Copy,
  ExternalLink,
  Globe,
  KeyRound,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTenant } from '@/services/tenant-context';

/**
 * Engage → Chatbot → Embed.
 *
 * Tenant-admin facing copy-paste instructions for putting the ZAFROI
 * chatbot widget on the tenant's own website. The page renders the
 * snippet pre-filled with the tenant's PORTAL key when available;
 * otherwise it shows a "request a portal key" CTA.
 *
 * The actual key issuance happens in Operations → Developer → Keys
 * (where every other ZAFROI API key lives). This page just generates
 * the script tag for copy/paste once a key is in hand.
 */
export function ChatbotEmbedPage() {
  const { currentTenant } = useTenant();
  const [position, setPosition] = useState<'bottom-right' | 'bottom-left'>('bottom-right');
  const [copied, setCopied] = useState(false);

  const snippet = useMemo(() => {
    return [
      '<!-- ZAFROI Engage chatbot — paste before </body> -->',
      '<script',
      '  src="https://engage.zafroi.com/widget/chat.js"',
      `  data-position="${position}"`,
      '  defer',
      '></script>',
    ].join('\n');
  }, [position]);

  const reactSnippet = useMemo(() => {
    return `// In your React app's entry component (e.g. App.tsx)
import { useEffect } from 'react';

export function ChatbotLoader() {
  useEffect(() => {
    const s = document.createElement('script');
    s.src = 'https://engage.zafroi.com/widget/chat.js';
    s.async = true;
    s.dataset.position = '${position}';
    document.body.appendChild(s);
    return () => { s.remove(); window.ZafroiChatbot = undefined; };
  }, []);
  return null;
}`;
  }, [position]);

  function copy(text: string) {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        toast.success('Copied to clipboard');
        setTimeout(() => setCopied(false), 1500);
      },
      () => toast.error('Copy failed — select the text manually'),
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm">
        <Link
          to="/chatbot"
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Chatbot
        </Link>
      </div>

      <PageHeader
        title="Embed the chatbot on your website"
        description={`Put the ZAFROI Engage chatbot on ${currentTenant.name}'s site with a single script tag. The widget renders an isolated chat bubble in the bottom corner, answers only from your approved knowledge base, and routes anything else to your support team.`}
      />

      <SectionHeader
        title="1. Register your website"
        description="The widget authenticates by your site's Origin. Make sure your Organization.website matches the domain you'll embed on — that's the only setup needed."
        withDivider
      />
      <DashboardCard icon={KeyRound} title="Where to set it">
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>
            Open <strong className="text-foreground">Operations → Workspace → Organizations</strong>,
            edit your org, and fill in the <strong className="text-foreground">Website</strong>{' '}
            field with the exact origin you'll embed on (e.g.{' '}
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[12px]">
              https://www.example.com
            </code>
            ). Allowlist updates within ~60 seconds — no redeploy needed.
          </p>
          <Button asChild variant="outline" size="sm">
            <a
              href="https://zafroi.com/platform/organizations"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5"
            >
              Open organizations in Operations
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </Button>
          <p className="text-[12.5px] text-muted-foreground">
            <strong className="text-foreground">No API key needed.</strong> The
            widget mints a short-lived per-conversation token by validating
            your site's Origin — no long-lived secret ever sits in your page
            source.
          </p>
        </div>
      </DashboardCard>

      <SectionHeader
        title="2. Configure & copy the snippet"
        description="Tweak placement; the snippet below updates live."
        withDivider
      />
      <DashboardCard>
        <div className="space-y-1.5 md:w-1/2">
          <label
            htmlFor="position"
            className="text-[12px] font-medium text-foreground"
          >
            Bubble position
          </label>
          <select
            id="position"
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-[13px] text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            value={position}
            onChange={(e) => setPosition(e.target.value as 'bottom-right' | 'bottom-left')}
          >
            <option value="bottom-right">Bottom-right (default)</option>
            <option value="bottom-left">Bottom-left</option>
          </select>
          <p className="text-[11.5px] text-muted-foreground">
            Affects only the launcher button. The open chat panel sizes to its
            corner.
          </p>
        </div>
      </DashboardCard>

      <DashboardCard
        title="Vanilla HTML"
        description="Paste this just before the closing </body> tag."
      >
        <SnippetBlock text={snippet} onCopy={copy} copied={copied} />
      </DashboardCard>

      <DashboardCard
        title="React app"
        description="Drop this into your root component if you'd rather control mount lifecycle."
      >
        <SnippetBlock text={reactSnippet} onCopy={copy} copied={copied} />
      </DashboardCard>

      <SectionHeader
        title="3. Paste on your site"
        description="That's it — no email, no allowlist ticket. Your origin is auto-allowed within ~60 seconds of being set on the organization."
        withDivider
      />
      <DashboardCard icon={Globe} title="What happens at runtime">
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>
            • The script loads from <code className="rounded bg-muted px-1 font-mono text-[12px]">engage.zafroi.com</code>{' '}
            and renders a launcher in the chosen corner.
          </li>
          <li>
            • On chat open, the widget POSTs to{' '}
            <code className="rounded bg-muted px-1 font-mono text-[12px]">/engage/widget/session</code>{' '}
            — the server reads the browser's Origin, matches it against your{' '}
            <code className="rounded bg-muted px-1 font-mono text-[12px]">Organization.website</code>,
            and mints a 30-min session token bound to that origin + tenant.
          </li>
          <li>
            • The token expires and refreshes automatically. No secrets in
            your HTML.
          </li>
        </ul>
      </DashboardCard>

      <SectionHeader
        title="What the widget can and can't do"
        description="Constrained-RAG safety: the bot answers ONLY from approved knowledge base entries, plus a few whitelisted intents (order status). Everything else gets a polite handoff."
        withDivider
      />
      <DashboardCard icon={ShieldCheck} title="Safety guarantees">
        <ul className="space-y-2 text-[13px] text-muted-foreground">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
            <span>
              <strong className="text-foreground">No hallucinations.</strong>{' '}
              Replies are grounded in your approved KB. If retrieval finds
              nothing, the bot declines and offers to connect a human.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
            <span>
              <strong className="text-foreground">Tier-locked.</strong> PORTAL
              keys can only hit chat endpoints — not your KB, not your
              gaps/usage analytics, not your knowledge management.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
            <span>
              <strong className="text-foreground">Auto-handoff.</strong> Every
              question the bot couldn't answer surfaces in your{' '}
              <Link to="/chatbot" className="text-primary hover:underline">
                Chatbot dashboard
              </Link>{' '}
              so you can grow the KB or hand off to support.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
            <span>
              <strong className="text-foreground">CSS-isolated.</strong> The
              widget renders inside a Shadow DOM — your site's stylesheets
              can't bleed into the chat UI and vice versa.
            </span>
          </li>
        </ul>
      </DashboardCard>
    </div>
  );
}

function SnippetBlock({
  text,
  onCopy,
  copied,
}: {
  text: string;
  onCopy: (text: string) => void;
  copied: boolean;
}) {
  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-md border border-border bg-muted/30 p-4 font-mono text-[12px] leading-snug text-foreground">
        {text}
      </pre>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="absolute right-2 top-2"
        onClick={() => onCopy(text)}
      >
        {copied ? (
          <>
            <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
            Copied
          </>
        ) : (
          <>
            <Copy className="mr-1 h-3.5 w-3.5" />
            Copy
          </>
        )}
      </Button>
    </div>
  );
}

// Used to keep the loader skeleton consistent in case we ever lazy-load.
export function ChatbotEmbedPageSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-32" />
      <Skeleton className="h-48" />
    </div>
  );
}
